import {Flags, openDialog, Parameter} from 'platform/components';
import {Heading, HStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query, useGetUserQuery} from '@dms/api/customer';
import {useGetRentalOrderQuery, useGetServiceOrderByRentalOrderIdQuery} from '@dms/api/rental';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetMetadaServiceCaseQuery, useGetServiceOrderQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {customerRoutes, testIds, workshopRoutes} from '@dms/routes';
import {
  composeAddressLineFromCommonAddress,
  EMPTY_PLACEHOLDER,
  getCustomerEmail,
  getCustomerName,
  getCustomerPhoneNumber,
  getUserName,
  isCustomerNaturalPerson,
  SnippetProps,
  useStateFlag,
} from '@dms/shared';

import {buildArray, composePath, parseDate} from 'shared/web';

import {PairWithServiceOrderForm} from '../../../components/PairWithServiceOrderForm';
import {getRentFromFlags} from '../utils/getRentFromFlags';
import {getRentToFlags} from '../utils/getRentToFlags';

export const useRentalOrderHeaderSnippets = (rentalOrderId?: string | null) => {
  const {data: rentalOrder, isLoading: isRentalOrderLoading} = useGetRentalOrderQuery(
    {
      rentalOrderId: rentalOrderId ?? '',
    },
    {skip: isNil(rentalOrderId)}
  );

  const {data: transactionData, isLoading: isLoadingTransaction} =
    useGetServiceOrderByRentalOrderIdQuery(
      {rentalOrderId: rentalOrderId ?? ''},
      {skip: isNil(rentalOrderId)}
    );
  const serviceOrderId = transactionData?.serviceOrderId ?? '';
  const serviceCaseId = transactionData?.serviceCaseId ?? '';

  const isServiceOrderPaired = isNotNil(transactionData);

  const {data: serviceOrder, isLoading: isLoadingServiceOrder} = useGetServiceOrderQuery(
    {serviceCaseId, serviceOrderId},
    {
      refetchOnMountOrArgChange: true,
      skip: isNilOrEmpty(serviceCaseId) || isNilOrEmpty(serviceOrderId),
    }
  );

  const {data: serviceCase, isLoading: isLoadingServiceCase} = useGetMetadaServiceCaseQuery(
    {serviceCaseId},
    {skip: isNilOrEmpty(serviceCaseId)}
  );

  const {data: serviceCaseCreatedBy, isLoading: isLoadingServiceCaseCreatedBy} = useGetUserQuery(
    {id: serviceCase?.createdBy ?? ''},
    {skip: isNilOrEmpty(serviceCase?.createdBy)}
  );

  const stateFlagProps = useStateFlag(serviceOrder?.state);

  const customerId = rentalOrder?.customerId;
  const formatDateTime = useDateTimeFormatter();

  const {data: customer, isLoading: isCustomerLoading} = useGetCustomerV2Query({
    customerId: customerId ?? '',
  });

  const areSnippetsLoading =
    isCustomerLoading ||
    isRentalOrderLoading ||
    isLoadingServiceOrder ||
    isLoadingTransaction ||
    isLoadingServiceCaseCreatedBy ||
    isLoadingServiceCase;

  if (isNil(rentalOrder) && !isRentalOrderLoading) {
    return {
      snippets: [
        {
          label: i18n.t('entity.customer.labels.customer'),
          placeholder: {title: i18n.t('page.rentalOrder.labels.notSelected'), isDisabled: true},
          'data-testid': testIds.rentalOrder.detail('customer'),
        },
        {
          label: i18n.t('page.rentalOrder.labels.rent'),
          placeholder: {title: i18n.t('page.rentalOrder.labels.notSelected'), isDisabled: true},
          'data-testid': testIds.rentalOrder.detail('rent'),
        },
        {
          label: i18n.t('page.rentalOrder.labels.return'),
          placeholder: {title: i18n.t('page.rentalOrder.labels.notSelected'), isDisabled: true},
          'data-testid': testIds.rentalOrder.detail('return'),
        },
        {
          label: i18n.t('page.rentalOrder.labels.serviceOrder'),
          placeholder: {
            title: i18n.t('page.rentalOrder.labels.pairWithServiceOrder'),
            onClick: () => {},
            isDisabled: true,
          },
          'data-testid': testIds.rentalOrder.detail('serviceOrder'),
        },
      ],
      areSnippetsLoading: false,
    };
  }

  const rentAddress =
    rentalOrder?.returnVehicle?.rentAddress ?? rentalOrder?.rentVehicle?.rentAddress;
  const returnAddress =
    rentalOrder?.returnVehicle?.returnAddress ??
    rentalOrder?.rentVehicle?.returnAddress ??
    rentalOrder?.rentVehicle?.rentAddress;
  const rentFrom =
    rentalOrder?.returnVehicle?.rentalPeriod?.from ?? rentalOrder?.plannedRentalPeriod?.from;
  const rentTo =
    rentalOrder?.returnVehicle?.rentalPeriod?.to ?? rentalOrder?.plannedRentalPeriod?.to;

  const snippets: SnippetProps[] = [
    {
      'data-testid': testIds.rentalOrder.detail('customer'),
      resourceId: EntityResourceIds.rentalOrder,
      recordId: rentalOrder?.rentalOrderId ?? '',
      label: i18n.t('entity.customer.labels.customer'),
      placeholder: {
        title: i18n.t('page.rentalOrder.labels.notSelected'),
      },
      title: getCustomerName(customer),
      parameters: buildArray<string | null>()
        .when(
          isNotNil(customer) && !isCustomerNaturalPerson(customer),
          getCustomerPhoneNumber(customer)
        )
        .when(isNotNil(customer) && !isCustomerNaturalPerson(customer), getCustomerEmail(customer))
        .when(
          isNotNil(customer) && isCustomerNaturalPerson(customer),
          getCustomerPhoneNumber(customer)
        )
        .when(isNotNil(customer) && isCustomerNaturalPerson(customer), getCustomerEmail(customer)),
      href: customer?.id
        ? composePath(customerRoutes.detail, {
            params: {id: customer?.id},
            isSearchParamsPreserved: false,
          })
        : undefined,
      customerId: customer?.id,
      selectedContactId: customer?.foundingPerson?.id,
    },
    {
      label: i18n.t('page.rentalOrder.labels.rent'),
      'data-testid': testIds.rentalOrder.detail('rent'),
      parameters: [
        rentAddress ? composeAddressLineFromCommonAddress(rentAddress) : EMPTY_PLACEHOLDER,
      ],
      title: (
        <HStack align="center" spacing={2}>
          <Heading size={5}>
            {rentFrom ? formatDateTime('dateTimeShort', parseDate(rentFrom)) : EMPTY_PLACEHOLDER}
          </Heading>
          <Flags flags={getRentFromFlags(rentalOrder)} size="small" />
        </HStack>
      ),
    },
    {
      label: i18n.t('page.rentalOrder.labels.return'),
      'data-testid': testIds.rentalOrder.detail('return'),
      parameters: [
        returnAddress ? composeAddressLineFromCommonAddress(returnAddress) : EMPTY_PLACEHOLDER,
      ],
      title: (
        <HStack align="center" spacing={2}>
          <Heading size={5}>
            {rentTo ? formatDateTime('dateTimeShort', parseDate(rentTo)) : EMPTY_PLACEHOLDER}
          </Heading>
          <Flags flags={getRentToFlags(rentalOrder)} size="small" />
        </HStack>
      ),
    },
    {
      label: i18n.t('page.rentalOrder.labels.serviceOrder'),
      'data-testid': testIds.rentalOrder.detail('serviceOrder'),
      placeholder: {
        title: i18n.t('page.rentalOrder.labels.pairWithServiceOrder'),
        isDisabled: rentalOrder?.state === 'draft' || isNil(rentalOrderId),
        onClick: () =>
          openDialog(<PairWithServiceOrderForm rentalOrderId={rentalOrderId ?? ''} />, {
            title: i18n.t('page.rentalOrder.labels.pairWithServiceOrder'),
            size: 'large',
          }),
      },
      title: isServiceOrderPaired ? (serviceOrder?.number ?? '') : undefined,
      onClick: () =>
        window.open(
          composePath(workshopRoutes.serviceCaseDetail, {
            params: {id: serviceOrder?.serviceCaseId},
            queryParams: {
              orderId: serviceOrder?.id,
            },
          }),
          '_blank'
        ),
      flags: stateFlagProps && isServiceOrderPaired ? [stateFlagProps] : [],
      parameters: buildArray<Parameter>()
        .when(
          isServiceOrderPaired && isNotNilOrEmpty(serviceCase?.createdAt),
          serviceCase?.createdAt
            ? formatDateTime('dateShort', parseDate(serviceCase?.createdAt))
            : ''
        )
        .when(
          isServiceOrderPaired && serviceCase?.createdBy,
          `${i18n.t('general.labels.createdBy')}: ${getUserName(serviceCaseCreatedBy)}`
        ),
    },
  ];

  return {
    snippets,
    areSnippetsLoading,
  };
};
